// De vier prioriteiten van de publieke quickscan — één bron voor het formulier,
// de klant-PDF en het adminportaal.
//
// Bewust zonder React, zonder lucide en zonder `@/`-alias: dit bestand wordt
// gelezen door lagen die geen Vite-resolver hebben (de node-testrunner draait
// TS direct, zonder alias-map) en door de PDF-generator die geen component-
// boom mag optuigen. Iconen horen daarom niet hier maar in het scherm.
//
// De sleutels hieronder zijn de sleutels die in public.quickscan_priorities
// staan (cost-savings, co2-reduction, reliability, independence). Wie ze
// hernoemt, breekt de historie van bestaande inzendingen.

export interface QuickscanPriority {
  id: string;
  label: string;
}

/** De schaal van de schuifjes. De PDF en het adminportaal delen door `max`. */
export const QUICKSCAN_PRIORITY_SCALE = {
  min: 1,
  max: 10,
  /** Waarde waarop elk schuifje start — ook als de bezoeker er niet aan komt. */
  default: 5,
} as const;

export const QUICKSCAN_PRIORITIES: readonly QuickscanPriority[] = [
  { id: 'cost-savings', label: 'Kosten besparen' },
  { id: 'co2-reduction', label: 'CO2-reductie' },
  { id: 'reliability', label: 'Betrouwbaarheid' },
  { id: 'independence', label: 'Energie-onafhankelijkheid' },
];

/**
 * De beginstand van het prioriteitenscherm, als state.
 *
 * Het scherm liet altijd al vier schuifjes op 5 zien, maar schreef pas iets weg
 * zodra er één werd versleept. Wie niets aanraakte, zond nul prioriteiten in en
 * kreeg een bonus van 0 in de score — het formulier loog dus over wat het
 * bewaarde. Daarom staat de standaard nu in de state en niet alleen in beeld.
 */
export function defaultQuickscanPriorities(): Record<string, number> {
  const defaults: Record<string, number> = {};
  for (const priority of QUICKSCAN_PRIORITIES) {
    defaults[priority.id] = QUICKSCAN_PRIORITY_SCALE.default;
  }
  return defaults;
}

/**
 * Nederlandse naam bij een opgeslagen sleutel. Onbekende sleutels (oude
 * inzendingen, toekomstige uitbreidingen) worden leesbaar gemaakt in plaats van
 * rauw afgedrukt, zodat er nooit meer "cost-savings" in een klantdocument staat.
 */
export function quickscanPriorityLabel(id: string): string {
  const known = QUICKSCAN_PRIORITIES.find((priority) => priority.id === id);
  if (known) return known.label;

  const words = id.replace(/[-_]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  if (!words) return id;
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

export interface OrderedQuickscanPriority {
  id: string;
  label: string;
  value: number;
}

/**
 * Opgeslagen prioriteiten in schermvolgorde, met hun label erbij. Onbekende
 * sleutels sluiten achteraan aan zodat er niets stilletjes wegvalt. Alle drie de
 * weergaves (PDF, admin-detail, CSV) lopen hierdoor in dezelfde volgorde.
 */
export function orderQuickscanPriorities(
  priorities: Record<string, number> | null | undefined,
): OrderedQuickscanPriority[] {
  if (!priorities) return [];

  const ordered: OrderedQuickscanPriority[] = [];
  for (const priority of QUICKSCAN_PRIORITIES) {
    if (priority.id in priorities) {
      ordered.push({ id: priority.id, label: priority.label, value: priorities[priority.id] });
    }
  }
  for (const id of Object.keys(priorities)) {
    if (!QUICKSCAN_PRIORITIES.some((priority) => priority.id === id)) {
      ordered.push({ id, label: quickscanPriorityLabel(id), value: priorities[id] });
    }
  }
  return ordered;
}

/** Percentage voor een balkje, begrensd zodat een 10 nooit 200% breed wordt. */
export function quickscanPriorityPercentage(value: number): number {
  const percentage = (Number(value) / QUICKSCAN_PRIORITY_SCALE.max) * 100;
  if (!Number.isFinite(percentage)) return 0;
  return Math.max(0, Math.min(100, percentage));
}
